// src/views/ViewStockSales.js
import React, { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import StockSalesService from "../services/StockSalesService";
import ProjectService from "../services/ProjectService";
import ItemsService from "../services/ItemsServices";
import StorageService from "../services/StorageService";
import Loading from "../components/loading";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import UnAuthorized from "../views/Unathorized";
import ReactPaginate from "react-paginate";
import MessageDialog from "../components/MessageDialog";
import utility from "../utils/utility";
import logo from "../assets/images/deviops_logo.png";

export default function ViewStockSales() {
  const companyId = StorageService.activeCompany()?.companyId;
  const employeeId = StorageService.loginData()?.employeeId;
  const roleId = StorageService.loginData()?.roleId;

  const [stockSales, setStockSales] = useState([]);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState(null);
  const [projectList, setProjectList] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const pRes = await ProjectService.getActive(companyId);
        const pData = pRes?.data || pRes || [];
        setProjectList(Array.isArray(pData) ? pData : []);
      } catch (e) {
        console.error("Failed to load projects:", e);
      }
    };
    if (companyId) {
      fetchProjects();
    }
  }, [companyId]);

  const formatProjectDisplay = (project) => {
    if (!project) return "-";
    let projObj = project;
    if (typeof project === "string" || typeof project === "number") {
      const match =
        projectList.find(
          (p) =>
            String(p.id) === String(project) ||
            String(p.projectId) === String(project) ||
            p.projectName === project
        ) || {};
      projObj = Object.keys(match).length ? match : { projectName: String(project) };
    }

    const projectId = projObj.projectId || projObj.id;
    const matchedProject =
      (projectId &&
        projectList.find(
          (p) => String(p.id) === String(projectId) || String(p.projectId) === String(projectId)
        )) ||
      (projObj.projectName &&
        projectList.find((p) => p.projectName === projObj.projectName)) ||
      {};

    const projectName =
      projObj.projectName || projObj.name || matchedProject.projectName || "";
    const categoryName =
      projObj.projectCategoryName ||
      projObj.categoryName ||
      projObj.category ||
      matchedProject.projectCategoryName ||
      matchedProject.categoryName ||
      matchedProject.category ||
      "";
    const projectCode =
      projObj.projectCode || matchedProject.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }
      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }
      return projectName;
    }
    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }
    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  // APPROVE
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveItem, setApproveItem] = useState(null);
  const [approveNote, setApproveNote] = useState("");

  // REJECT
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  // CANCEL
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelItem, setCancelItem] = useState(null);

  const [popup, setPopup] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [itemMap, setItemMap] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [allStockSales, setAllStockSales] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const itemsPerPage = 10;

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      setSearch(searchText);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const navigate = useNavigate();
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Stock-Sales-Invoice"
  });

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const { permission, can } = usePermission(
    ScreenData.Stock_Out,
    showMessage
  );

  useEffect(() => {
    if (permission === null) return;
    if (permission.canView !== 1) {
      setLoading(false);
      return;
    }
    loadAll();
  }, [companyId, employeeId, roleId, currentPage, permission, search, statusFilter]);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setStockSales(allStockSales.slice(start, end));
  }, [allStockSales, currentPage]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const res = await StockSalesService.getAll(
        companyId,
        employeeId,
        roleId,
        1,
        1000,
        search,
        statusFilter
      );
      const rows = res?.data?.data || [];
      const sortedRows = [...rows].sort((a, b) => b.id - a.id);
      setAllStockSales(sortedRows);
      const totalPages = Math.max(1, Math.ceil(sortedRows.length / itemsPerPage));
      setPageCount(totalPages);
      const safePage = Math.min(currentPage, totalPages);
      if (safePage !== currentPage) {
        setCurrentPage(safePage);
      }
      const start = (safePage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      setStockSales(sortedRows.slice(start, end));
      setSelectedId(null);
    } catch (err) {
      console.error("LOAD ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => navigate("/cpnl/stocksales/add");

  const handleModify = () => {
    if (!selectedId) {
      return showMessage("Please select one record to modify.");
    }
    const selectedRecord = stockSales.find(s => s.id === selectedId);
    if (!selectedRecord) {
      return showMessage("Selected record not found.");
    }
    if (selectedRecord.status !== "pending") {
      return showMessage("Only PENDING records can be modified.");
    }
    navigate(`/cpnl/stocksales/add/${selectedId}`);
  };

  const handleView = () => {
    if (!selectedId) return showMessage("Please select one record to view details.");
    loadPopup(selectedId);
  };

  const submitApprove = async () => {
    if (!approveNote.trim()) {
      showMessage("Please enter approval note");
      return;
    }
    try {
      await StockSalesService.approve(approveItem.id, {
        approvedBy: employeeId,
        approvedNotes: approveNote
      });
      setStockSales(prev =>
        prev.map(r =>
          r.id === approveItem.id ? { ...r, status: "approved" } : r
        )
      );
      showMessage("Stock Sales approved successfully", "success");
      setShowApproveModal(false);
    } catch (err) {
      console.error("Approve error:", err.response?.data || err);
      showMessage(err.response?.data?.message || "Approval failed");
    }
  };

  const submitReject = async () => {
    if (!rejectNote.trim()) {
      showMessage("Please enter rejection note");
      return;
    }
    try {
      await StockSalesService.reject(rejectItem.id, {
        rejectedBy: employeeId,
        rejectedNotes: rejectNote
      });
      setStockSales(prev =>
        prev.map(r =>
          r.id === rejectItem.id ? { ...r, status: "rejected" } : r
        )
      );
      showMessage("Stock Sales rejected successfully", "success");
      setShowRejectModal(false);
    } catch (err) {
      console.error("Reject error:", err.response?.data || err);
      showMessage(err.response?.data?.message || "Rejection failed");
    }
  };

  const submitCancel = async () => {
    try {
      await StockSalesService.cancel(cancelItem.id, {
        cancelledBy: employeeId
      });
      setStockSales(prev =>
        prev.map(r =>
          r.id === cancelItem.id ? { ...r, status: "cancelled" } : r
        )
      );
      showMessage("Stock Sales cancelled successfully", "success");
      setShowCancelModal(false);
    } catch (err) {
      console.error("Cancel error:", err.response?.data || err);
      showMessage(err.response?.data?.message || "Cancel failed");
    }
  };

  const loadPopup = async (id) => {
    try {
      setPopup(true);
      setPopupLoading(true);

      let localItemMap = { ...itemMap };
      if (!Object.keys(localItemMap).length) {
        const itemRes = await ItemsService.getAllItems();
        const iMap = {};
        (itemRes?.data || itemRes || []).forEach((i) => {
          const key = String(i.id ?? i.itemId ?? i.item_id ?? "");
          iMap[key] = {
            name: i.itemName ?? i.name ?? "",
            categoryName: i.itemCategoryName ?? i.categoryName ?? i.itemCategory ?? "",
            description: i.itemDescription ?? i.description ?? ""
          };
        });
        setItemMap(iMap);
        localItemMap = iMap;
      }

      const res = await StockSalesService.getById(id);
      const payload = res?.data?.data ?? res?.data ?? res ?? {};
      const selectedRecord =
        stockSales.find((r) => r.id === id) ||
        allStockSales.find((r) => r.id === id) ||
        {};
      const mergedHeader = {
        ...selectedRecord,
        ...payload,
        projectName: payload.projectName || selectedRecord.projectName || "",
        projectCode: payload.projectCode || selectedRecord.projectCode || "",
        projectCategoryName:
          payload.projectCategoryName ||
          selectedRecord.projectCategoryName ||
          "",
        projectCategoryCode:
          payload.projectCategoryCode ||
          selectedRecord.projectCategoryCode ||
          "",
      };
      setHeader(mergedHeader);

      const details = payload.details || [];
      const mapped = (Array.isArray(details) ? details : []).map((d) => {
        const itemIdKey = String(d.itemId ?? "");
        const item = localItemMap[itemIdKey] || {};
        return {
          itemId: d.itemId,
          itemCode: d.itemCode ?? "",
          itemName: item.name ?? "",
          itemCategoryName: d.itemCategoryName || d.categoryName || d.itemCategory || item.categoryName || "",
          itemDescription: item.description ?? "",
          UOM: d.UOM,
          quantity: d.quantity,
        };
      });

      setDetailRows(mapped);
    } catch (err) {
      console.error("POPUP LOAD ERROR:", err);
      setDetailRows([]);
    } finally {
      setPopupLoading(false);
    }
  };

  if (permission === null) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!permission || permission.canView !== 1) {
    return <UnAuthorized />;
  }

  const formatEmployee = (name, code) => {
    if (name && code) return `${name} (${code})`;
    return name || code || "-";
  };

  return (
    <>
      <Breadcrumb pageTitle="Stock Sales" secondTitle="Inventory" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="card p-3 shadow-sm">
        <div className="mb-3 pb-2 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
          {can("canAdd") && (
            <button className="btn btn-success me-2" onClick={handleAdd}>
              + Add
            </button>
          )}
          {can("canModify") && (
            <button
              className="btn btn-warning me-2"
              onClick={handleModify}
              disabled={
                loading ||
                !selectedId ||
                stockSales.find(s => s.id === selectedId)?.status !== "pending"
              }
            >
              <i className="ti ti-edit me-1"></i> Modify
            </button>
          )}
          {can("canView") && (
            <>
              <button className="btn btn-primary me-2" onClick={handleView}>
                <i className="ti ti-eye me-1"></i> View
              </button>

              <select
                className="form-select d-inline w-auto"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </>
          )}

          <div className="d-flex align-items-center ms-auto">
            <div className="position-relative">
              <input
                type="text"
                className="form-control pe-5"
                placeholder="Search Project / Invoice No"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "280px" }}
              />
              <i
                className="ti ti-search position-absolute"
                style={{
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d"
                }}
              ></i>
            </div>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>Select</th>
                <th>Invoice No</th>
                <th>Sale Date</th>
                <th>Project</th>
                <th>Store</th>
                <th>Customer</th>
                <th>Customer Store</th>
                <th>Transporter</th>
                <th>Vehicle No</th>
                <th>Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {stockSales.map((s) => (
                <tr key={s.id}>
                  <td>
                    <input
                      type="radio"
                      name="stockSalesSelect"
                      checked={selectedId === s.id}
                      onChange={() => setSelectedId(s.id)}
                    />
                  </td>
                  <td>{s.invoiceNo ?? "-"}</td>
                  <td>
                    {s.saleDate
                      ? utility.formatDateDDMMMYYYY(s.saleDate)
                      : "-"}
                  </td>
                  <td>{formatProjectDisplay(s)}</td>
                  <td>{s.storeName}</td>
                  <td>{s.customerName || s.customerId || "-"}</td>
                  <td>{s.customerStoreName || s.customerStoreId || "-"}</td>
                  <td>{s.transporterName ?? "-"}</td>
                  <td>{s.vehicleNo ?? "-"}</td>
                  <td>
                    <span
                      className={`badge ${s.status === "pending"
                        ? "bg-info text-dark"
                        : s.status === "approved"
                          ? "bg-success"
                          : s.status === "rejected"
                            ? "bg-danger"
                            : s.status === "cancelled"
                              ? "bg-dark"
                              : "bg-secondary"
                        }`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="text-center d-flex justify-content-center gap-2">
                    {can("canApprove") && (
                      <button
                        className="btn btn-success btn-sm"
                        title="Approve"
                        disabled={s.status !== "pending"}
                        onClick={() => {
                          setApproveItem(s);
                          setApproveNote("");
                          setShowApproveModal(true);
                        }}
                      >
                        <i className="ti ti-circle-check"></i>
                      </button>
                    )}

                    {can("canReject") && (
                      <button
                        className="btn btn-danger btn-sm"
                        title="Reject"
                        disabled={s.status !== "pending"}
                        onClick={() => {
                          setRejectItem(s);
                          setRejectNote("");
                          setShowRejectModal(true);
                        }}
                      >
                        <i className="ti ti-circle-x"></i>
                      </button>
                    )}

                    {can("canCancel") && (
                      <button
                        className="btn btn-secondary btn-sm"
                        title="Cancel"
                        disabled={s.status !== "pending" || Number(s.createdBy) !== Number(employeeId)}
                        onClick={() => {
                          setCancelItem(s);
                          setShowCancelModal(true);
                        }}
                      >
                        <i className="ti ti-cancel"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}

              {!loading && stockSales.length === 0 && (
                <tr>
                  <td colSpan="11" className="text-center">
                    No records found
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan="11" className="text-center py-4">
                    <Loading />
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <ReactPaginate
            forcePage={Math.max(0, Math.min(currentPage - 1, Math.max(pageCount - 1, 0)))}
            onPageChange={(e) => setCurrentPage(e.selected + 1)}
            pageCount={Math.max(1, pageCount)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            previousLabel="< Previous"
            nextLabel="Next >"
            containerClassName="pagination-container"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            nextClassName="page-item"
            activeClassName="active"
          />
        </div>
      </div>

      {/* POPUP */}
      {popup && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            background: "rgba(0,0,0,0.5)",
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Stock Sales Details</h5>
                <button className="btn-close" onClick={() => setPopup(false)} />
              </div>

              <div className="modal-body">
                {popupLoading ? (
                  <div className="text-center p-4">
                    <Loading />
                  </div>
                ) : (
                  <>
                    {header && (
                      <div className="p-3">
                        <div className="row mb-3">
                          {/* Row 1 */}
                          <div className="col-md-6">
                            <strong>Project Name:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{formatProjectDisplay(header)}</span>
                          </div>
                          <div className="col-md-6">
                            <strong>Store Name:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{header.storeName || "-"}</span>
                          </div>

                          {/* Row 2 */}
                          <div className="col-md-6 mt-2">
                            <strong>Customer Name:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{header.customerName || header.customerId || "-"}</span>
                          </div>
                          <div className="col-md-6 mt-2">
                            <strong>Customer Store:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{header.customerStoreName || header.customerStoreId || "-"}</span>
                          </div>

                          {/* Row 3 */}
                          <div className="col-md-6 mt-2">
                            <strong>Invoice Number:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{header.invoiceNo || "-"}</span>
                          </div>
                          <div className="col-md-6 mt-2">
                            <strong>Invoice Date:</strong> <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>{header.invoiceDate ? utility.formatDateDDMMMYYYY(header.invoiceDate) : "-"}</span>
                          </div>

                          {/* Row 4 */}
                          <div className="col-md-6 mt-2">
                            <strong>Transporter:</strong> {header.transporterName || "-"}
                          </div>
                          <div className="col-md-6 mt-2">
                            <strong>Vehicle Number:</strong> {header.vehicleNo || "-"}
                          </div>

                          {/* Row 5 */}
                          <div className="col-md-6 mt-2">
                            <strong>Sale Date:</strong>{" "}
                            <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                              {header.saleDate ? utility.formatDateDDMMMYYYY(header.saleDate) : "-"}
                            </span>
                          </div>
                          <div className="col-md-6 mt-2">
                            <strong>Notes:</strong> {header.stockSalesNotes || "-"}
                          </div>

                          {/* Row 6 */}
                          <div className="col-md-6 mt-2">
                            <strong>Created By:</strong> {formatEmployee(header.createdByName, header.createdByCode)}
                          </div>
                          <div className="col-md-6 mt-2">
                            <strong>Status:</strong>{" "}
                            <span style={{ fontWeight: 800, color: "#000", fontSize: "15px" }}>
                              {header.status ? header.status.toUpperCase() : "PENDING"}
                            </span>
                          </div>

                          {/* Conditional Action Notes */}
                          {header.status?.toLowerCase() === "approved" && (
                            <>
                              <div className="col-md-6 mt-2">
                                <strong>Approved By:</strong> {formatEmployee(header.approvedByName, header.approvedByCode)}
                              </div>
                              <div className="col-md-6 mt-2">
                                <strong>Approved Notes:</strong> {header.approvedNotes || "-"}
                              </div>
                            </>
                          )}

                          {header.status?.toLowerCase() === "rejected" && (
                            <>
                              <div className="col-md-6 mt-2">
                                <strong>Rejected By:</strong> {formatEmployee(header.rejectedByName, header.rejectedByCode)}
                              </div>
                              <div className="col-md-6 mt-2">
                                <strong>Rejected Notes:</strong> {header.rejectedNotes || "-"}
                              </div>
                            </>
                          )}

                          {header.status?.toLowerCase() === "cancelled" && (
                            <>
                              <div className="col-md-6 mt-2">
                                <strong>Cancelled By:</strong> {formatEmployee(header.cancelledByName, header.cancelledByCode)}
                              </div>
                              <div className="col-md-6 mt-2">
                                <strong>Cancelled Notes:</strong> {header.cancelledNotes || "-"}
                              </div>
                            </>
                          )}
                        </div>

                        <table className="table table-bordered mt-3">
                          <thead>
                            <tr>
                              <th>Item Category</th>
                              <th>Item</th>
                              <th>Item Description</th>
                              <th>UOM</th>
                              <th>Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailRows.map((d, i) => (
                              <tr key={i}>
                                <td>{d.itemCategoryName || "-"}</td>
                                <td>{d.itemCode}</td>
                                <td>{d.itemName || d.itemDescription}</td>
                                <td>{d.UOM}</td>
                                <td>{Number(d.quantity).toFixed(3)}</td>
                              </tr>
                            ))}
                            {detailRows.length === 0 && (
                              <tr>
                                <td colSpan="5" className="text-center">
                                  No Records Found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* PRINT TEMPLATE */}
                    <div style={{ display: "none" }}>
                      <div ref={printRef} className="print-container">

                        <div className="print-top-header">
                          <div className="print-top-left">
                            <img src={logo} alt="Logo" className="print-logo" />
                            <span className="print-company">{StorageService.activeCompany()?.companyName}</span>
                          </div>
                          <div className="print-top-right">
                            <b>Printed Date:</b> {utility.formatExportDateTime(new Date())}
                          </div>
                        </div>

                        <h3 className="print-title">Stock Sales Invoice</h3>

                        <table className="print-header">
                          <tbody>
                            <tr className="project-store-row">
                              <td><b>Project Name:</b> {formatProjectDisplay(header)}</td>
                              <td><b>Store Name:</b> {header?.storeName || "-"}</td>
                            </tr>

                            <tr>
                              <td><b>Customer Name:</b> {header?.customerName || header?.customerId || "-"}</td>
                              <td><b>Customer Store:</b> {header?.customerStoreName || header?.customerStoreId || "-"}</td>
                            </tr>

                            <tr>
                              <td><b>Invoice Number:</b> {header?.invoiceNo || "-"}</td>
                              <td><b>Invoice Date:</b> {header?.invoiceDate ? utility.formatDateDDMMMYYYY(header.invoiceDate) : "-"}</td>
                            </tr>

                            <tr>
                              <td><b>Transporter:</b> {header?.transporterName || "-"}</td>
                              <td><b>Vehicle Number:</b> {header?.vehicleNo || "-"}</td>
                            </tr>

                            <tr>
                              <td><b>Sale Date:</b> {header?.saleDate ? utility.formatDateDDMMMYYYY(header.saleDate) : "-"}</td>
                              <td><b>Notes:</b> {header?.stockSalesNotes || "-"}</td>
                            </tr>

                            <tr>
                              <td><b>Status:</b> {header?.status ? header.status.toUpperCase() : "PENDING"}</td>
                              <td><b>Created By:</b> {formatEmployee(header?.createdByName, header?.createdByCode)}</td>
                            </tr>

                            {header?.status?.toLowerCase() === "approved" && (
                              <tr>
                                <td><b>Approved By:</b> {formatEmployee(header?.approvedByName, header?.approvedByCode)}</td>
                                <td><b>Approved Notes:</b> {header?.approvedNotes || "-"}</td>
                              </tr>
                            )}

                            {header?.status?.toLowerCase() === "rejected" && (
                              <tr>
                                <td><b>Rejected By:</b> {formatEmployee(header?.rejectedByName, header?.rejectedByCode)}</td>
                                <td><b>Rejected Notes:</b> {header?.rejectedNotes || "-"}</td>
                              </tr>
                            )}

                            {header?.status?.toLowerCase() === "cancelled" && (
                              <tr>
                                <td><b>Cancelled By:</b> {formatEmployee(header?.cancelledByName, header?.cancelledByCode)}</td>
                                <td><b>Cancelled Notes:</b> {header?.cancelledNotes || "-"}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        <table className="print-table">
                          <thead>
                            <tr>
                              <th className="text-center">S.No</th>
                              <th className="text-start">Item Category</th>
                              <th className="text-start">Item Code</th>
                              <th className="text-start">UOM</th>
                              <th className="text-end">Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailRows.map((d, i) => (
                              <tr key={i}>
                                <td className="text-center">{i + 1}</td>
                                <td className="text-start">{d.itemCategoryName || "-"}</td>
                                <td className="text-start">{d.itemCode}</td>
                                <td className="text-start">{d.UOM}</td>
                                <td className="text-end">{Number(d.quantity).toFixed(3)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div style={{ marginTop: "60px", display: "flex", justifyContent: "space-between" }}>
                          <div>Prepared By Signature</div>
                          <div>Store Incharge Signature</div>
                          <div>Approved By Signature</div>
                        </div>

                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={handlePrint}
                  disabled={popupLoading || !header}
                >
                  <i className="ti ti-printer me-1"></i>
                  Print
                </button>
                <button className="btn btn-secondary" onClick={() => setPopup(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPROVE MODAL */}
      {showApproveModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Approve Stock Sales</h5>
                <button className="btn-close" onClick={() => setShowApproveModal(false)} />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to approve Stock Sales invoice <strong>{approveItem?.invoiceNo}</strong>?</p>
                <div className="mb-3">
                  <label className="form-label">Approval Notes<span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={approveNote}
                    onChange={(e) => setApproveNote(e.target.value)}
                    placeholder="Enter approval notes"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-success" onClick={submitApprove}>Approve</button>
                <button className="btn btn-secondary" onClick={() => setShowApproveModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {showRejectModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Reject Stock Sales</h5>
                <button className="btn-close" onClick={() => setShowRejectModal(false)} />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to reject Stock Sales invoice <strong>{rejectItem?.invoiceNo}</strong>?</p>
                <div className="mb-3">
                  <label className="form-label">Rejection Notes<span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    placeholder="Enter rejection notes"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-danger" onClick={submitReject}>Reject</button>
                <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {showCancelModal && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Cancel Stock Sales</h5>
                <button className="btn-close" onClick={() => setShowCancelModal(false)} />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to cancel Stock Sales invoice <strong>{cancelItem?.invoiceNo}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-danger" onClick={submitCancel}>Cancel</button>
                <button className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
